import { IonIcon, IonSpinner } from "@ionic/react";
import { alertCircleOutline, arrowBackOutline, checkmarkCircleOutline, returnDownBackOutline } from "ionicons/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TicketPurchaseApi } from "../../services/TicketPurchaseApi";
import { purchasedItinerariesSlice, usePersistedSlice, usePersistedSliceSelector } from "../../store/persistence";
import { displayCurrencyToIsEuro, pricingPrefsSlice } from "../../store/persistence/slices/pricingPrefs.slice";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { triggerSuccessHaptic } from "../../tools/haptics.tools";
import { applyBookingRefundToEntries, applyCreditNotesToEntries } from "../../tools/purchasedItineraries.tools";
import type {
    BookingRefundPreviewResponseDTO,
    TicketNonRefundableReason,
    TicketRefundPreviewItemDTO,
} from "../../types/dtos/api/purchase/purchase-refund.dto";
import PriceConversion from "../ItinerariesPlanner/pricing/PriceConversion";
import { readPurchaseError } from "../TicketPurchase/ticketPurchaseOfferRefresh.tools";
import { formatPaymentMethod, type TicketViewerItem } from "./ticketViewer.tools";

/**
 * Props of {@link TicketRefundPanel}.
 */
export interface TicketRefundPanelProps {
    "bookingId":       string;
    "initialTicketId": string;
    "items":           readonly TicketViewerItem[];
    "isDarkUi":        boolean;
    "onBack":          () => void;
    "onRefundSuccess": () => void;
}

/**
 * Human-readable French explanation for each normalized non-refundable reason.
 *
 * @param reason - Backend non-refundable reason code.
 * @returns User-facing explanation.
 */
export const describeNonRefundableReason = ( reason: TicketNonRefundableReason | undefined ): string => {
    switch ( reason ) {
        case "already_refunded":
            return "Ce billet a déjà été remboursé.";

        case "expired":
            return "Ce billet est expiré et ne peut plus être remboursé.";

        case "validity_started":
            return "La période de validité de ce billet a déjà commencé. Le remboursement en ligne n'est possible qu'avant le début de validité.";

        default:
            return "Ce billet n'est plus remboursable selon les conditions tarifaires CFF (les billets dégriffés ne sont remboursables que dans les 30 minutes suivant l'achat).";
    }
};

/**
 * Short badge label for a non-refundable ticket in a multi-ticket list.
 *
 * @param reason - Backend non-refundable reason code.
 * @returns Short badge text.
 */
const shortNonRefundableBadge = ( reason: TicketNonRefundableReason | undefined ): string => {
    switch ( reason ) {
        case "already_refunded":
            return "Déjà remboursé";

        case "expired":
            return "Expiré";

        case "validity_started":
            return "Validité commencée";

        default:
            return "Non remboursable";
    }
};

/**
 * Refund preview and confirmation panel rendered inside {@link TicketViewerModal}
 * when the shopper taps « Rembourser ce billet » on the back of a ticket card.
 *
 * @param props - Component props.
 * @returns The refund panel.
 */
export const TicketRefundPanel: React.FC<TicketRefundPanelProps> = ( {
    bookingId,
    initialTicketId,
    items,
    isDarkUi,
    onBack,
    onRefundSuccess,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const isEuro = usePersistedSliceSelector( pricingPrefsSlice, ( prefs ) => displayCurrencyToIsEuro( prefs.displayCurrency ) );
    const [ , setPurchasedItineraries ] = usePersistedSlice( purchasedItinerariesSlice );

    const [ loading, setLoading ] = useState( true );
    const [ preview, setPreview ] = useState<BookingRefundPreviewResponseDTO | null>( null );
    const [ selectedTicketIds, setSelectedTicketIds ] = useState<string[]>( [ initialTicketId ] );
    const [ submitting, setSubmitting ] = useState( false );
    const [ errorMessage, setErrorMessage ] = useState<string | null>( null );
    const [ noticeMessage, setNoticeMessage ] = useState<string | null>( null );

    const loadPreview = useCallback( async( showOfferChangedNotice: boolean ): Promise<void> => {
        setLoading( true );
        setErrorMessage( null );

        try {
            const response = await TicketPurchaseApi.getPurchaseBookingRefundPreview( bookingId );
            setPreview( response );

            const eligibleSet = new Set( response.tickets.
                filter( ( ticket ) => ticket.eligible && ( ticket.refundableAmountCents ?? 0 ) > 0 ).
                map( ( ticket ) => ticket.providerTicketId ) );

            setSelectedTicketIds( ( previous ) => {
                const kept = previous.filter( ( id ) => eligibleSet.has( id ) );

                if ( kept.length > 0 ) {
                    return kept;
                }

                if ( eligibleSet.has( initialTicketId ) ) {
                    return [ initialTicketId ];
                }

                const firstEligible = response.tickets.find( ( ticket ) => eligibleSet.has( ticket.providerTicketId ) );

                return firstEligible === undefined ? [] : [ firstEligible.providerTicketId ];
            } );

            if ( showOfferChangedNotice ) {
                setNoticeMessage( "Les conditions de remboursement ont évolué. Veuillez vérifier le nouveau montant avant de confirmer." );
            }
        } catch {
            setErrorMessage( "Impossible de vérifier les conditions de remboursement auprès des CFF. Veuillez réessayer." );
        } finally {
            setLoading( false );
        }
    }, [ bookingId, initialTicketId ] );

    useEffect( () => {
        void loadPreview( false );
    }, [ loadPreview ] );

    const itemByTicketId = useMemo( () => {
        const map = new Map<string, TicketViewerItem>();

        for ( const item of items ) {
            if ( !map.has( item.ticket.providerTicketId ) ) {
                map.set( item.ticket.providerTicketId, item );
            }
        }

        return map;
    }, [ items ] );

    const previewTickets = preview?.tickets ?? [];
    const initialPreviewTicket = previewTickets.find( ( ticket ) => ticket.providerTicketId === initialTicketId ) ?? previewTickets[0];
    const eligibleTickets = previewTickets.filter( ( ticket ) => ticket.eligible && ( ticket.refundableAmountCents ?? 0 ) > 0 );
    const selectedPreviewTickets = previewTickets.filter( ( ticket ) => ticket.eligible && selectedTicketIds.includes( ticket.providerTicketId ) );

    const totalOriginalCents = selectedPreviewTickets.reduce( ( sum, ticket ) => sum + ( ticket.originalPriceCents ?? 0 ), 0 );
    const totalRefundableCents = selectedPreviewTickets.reduce( ( sum, ticket ) => sum + ( ticket.refundableAmountCents ?? 0 ), 0 );
    const totalRetainedFeeCents = selectedPreviewTickets.reduce( ( sum, ticket ) => sum + ( ticket.retainedFeeCents ?? 0 ), 0 );

    const toggleTicketSelection = ( providerTicketId: string ): void => {
        setSelectedTicketIds( ( previous ) => ( previous.includes( providerTicketId )
            ? previous.filter( ( id ) => id !== providerTicketId )
            : [ ...previous, providerTicketId ] ) );
    };

    const handleConfirmRefund = async(): Promise<void> => {
        if ( selectedPreviewTickets.length === 0 || submitting ) {
            return;
        }

        setSubmitting( true );
        setErrorMessage( null );
        setNoticeMessage( null );

        try {
            const result = await TicketPurchaseApi.postPurchaseBookingRefund( bookingId, {
                "items": selectedPreviewTickets.map( ( ticket: TicketRefundPreviewItemDTO ) => ( {
                    "providerTicketId":              ticket.providerTicketId,
                    "expectedRefundableAmountCents": ticket.refundableAmountCents ?? 0,
                    "refundOfferIdentifier":         ticket.refundOfferIdentifier,
                } ) ),
            } );

            setPurchasedItineraries( ( previous ) => applyBookingRefundToEntries( previous, bookingId, result.tickets ) );

            /*
             * The credit note issues best-effort server-side at refund
             * confirmation — refresh the persisted list so the documents
             * popover shows it without waiting for the next bookings sync.
             */
            void TicketPurchaseApi.getPurchaseBookingCreditNotes( bookingId ).
                then( ( creditNotes ) => setPurchasedItineraries( ( previous ) => applyCreditNotesToEntries( previous, bookingId, creditNotes ) ) ).
                catch( () => undefined );
            void triggerSuccessHaptic();
            onRefundSuccess();
        } catch( error: unknown ) {
            const { status, code } = readPurchaseError( error );

            if ( status === 409 && ( code === "REFUND_OFFER_CHANGED" || code === "REFUND_NO_LONGER_POSSIBLE" ) ) {
                await loadPreview( true );
                setSubmitting( false );

                return;
            }

            setErrorMessage( "Le remboursement n'a pas pu aboutir. Aucun billet n'a été annulé ; veuillez réessayer." );
            setSubmitting( false );
        }
    };

    const paymentMethodLabel = formatPaymentMethod( preview?.paymentMethod );

    return (
        <div
            className="flex h-full min-h-0 flex-col justify-between overflow-y-auto p-4 md:p-6"
            data-testid="ticket-refund-panel"
            style={{
                "backgroundColor": isDarkUi ? palette.overlayCardBackground : "#F8FAFC",
                "color":           palette.overlayTextPrimary,
            }}
        >
            <div className="mx-auto flex w-full max-w-[420px] flex-col gap-4">
                <div className="flex items-center gap-2">
                    <button
                        aria-label="Retour au billet"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-solid transition-transform duration-150 ease-out active:scale-95"
                        data-testid="ticket-refund-back"
                        disabled={submitting}
                        onClick={onBack}
                        style={{
                            "borderColor": palette.overlayBorderStrong,
                            "background":  isDarkUi ? palette.overlayCardBackgroundStrong : "#FFFFFF",
                            "color":       palette.overlayTextPrimary,
                        }}
                        type="button"
                    >
                        <IonIcon
                            aria-hidden={true}
                            className="h-4 w-4"
                            icon={arrowBackOutline}
                        />
                    </button>
                    <h3 className="text-base font-bold">
                        {previewTickets.length > 1 ? "Remboursement des billets" : "Remboursement du billet"}
                    </h3>
                </div>
                {loading
                    ? (
                        <div
                            className="flex flex-col items-center justify-center gap-3 rounded-2xl border p-8 text-center"
                            data-testid="ticket-refund-loading"
                            style={{
                                "borderColor":     palette.overlayBorder,
                                "backgroundColor": isDarkUi ? palette.overlayCardBackgroundStrong : "#FFFFFF",
                            }}
                        >
                            <IonSpinner name="crescent" />
                            <p
                                className="text-xs font-medium"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
                                Vérification des conditions de remboursement auprès des CFF...
                            </p>
                        </div>
                    )
                    : null}
                {!loading && errorMessage !== null
                    ? (
                        <div
                            className="flex items-start gap-2.5 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-xs text-red-600 dark:text-red-300"
                            data-testid="ticket-refund-error"
                            role="alert"
                        >
                            <IonIcon
                                aria-hidden={true}
                                className="mt-0.5 h-4 w-4 shrink-0"
                                icon={alertCircleOutline}
                            />
                            <span>
                                {errorMessage}
                            </span>
                        </div>
                    )
                    : null}
                {!loading && noticeMessage !== null
                    ? (
                        <div
                            className="flex items-start gap-2.5 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-xs text-amber-700 dark:text-amber-300"
                            data-testid="ticket-refund-notice"
                            role="status"
                        >
                            <IonIcon
                                aria-hidden={true}
                                className="mt-0.5 h-4 w-4 shrink-0"
                                icon={alertCircleOutline}
                            />
                            <span>
                                {noticeMessage}
                            </span>
                        </div>
                    )
                    : null}
                {!loading && preview !== null && eligibleTickets.length === 0
                    ? (
                        <div
                            className="flex flex-col gap-3 rounded-2xl border p-5"
                            data-testid="ticket-refund-ineligible"
                            style={{
                                "borderColor":     palette.overlayBorder,
                                "backgroundColor": isDarkUi ? palette.overlayCardBackgroundStrong : "#FFFFFF",
                            }}
                        >
                            <div className="flex items-center gap-2.5 text-amber-600 dark:text-amber-400">
                                <IonIcon
                                    aria-hidden={true}
                                    className="h-5 w-5 shrink-0"
                                    icon={alertCircleOutline}
                                />
                                <span className="text-sm font-bold">
                                    Remboursement non disponible
                                </span>
                            </div>
                            <p
                                className="text-xs leading-relaxed"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
                                {describeNonRefundableReason( initialPreviewTicket?.nonRefundableReason )}
                            </p>
                        </div>
                    )
                    : null}
                {!loading && preview !== null && eligibleTickets.length > 0
                    ? (
                        <>
                            {previewTickets.length > 1
                                ? (
                                    <div
                                        className="flex flex-col gap-2 rounded-2xl border p-3.5"
                                        data-testid="ticket-refund-multi-list"
                                        style={{
                                            "borderColor":     palette.overlayBorder,
                                            "backgroundColor": isDarkUi ? palette.overlayCardBackgroundStrong : "#FFFFFF",
                                        }}
                                    >
                                        <p
                                            className="text-xs font-semibold uppercase tracking-wider"
                                            style={{ "color": palette.overlayTextSecondary }}
                                        >
                                            Billets de la réservation
                                        </p>
                                        {previewTickets.map( ( previewTicket ) => {
                                            const viewerItem = itemByTicketId.get( previewTicket.providerTicketId );
                                            const title = viewerItem?.details?.productDescription ?? "Billet CFF";
                                            const route = viewerItem?.fromName !== undefined && viewerItem?.toName !== undefined
                                                ? `${ viewerItem.fromName } → ${ viewerItem.toName }`
                                                : viewerItem?.details?.routeSummary ?? `#${ previewTicket.providerTicketId }`;
                                            const isChecked = selectedTicketIds.includes( previewTicket.providerTicketId );

                                            return (
                                                <label
                                                    className={`flex items-center justify-between gap-3 rounded-xl border p-2.5 text-xs transition-colors ${ previewTicket.eligible ? "cursor-pointer" : "cursor-not-allowed opacity-60" }`}
                                                    data-testid={`ticket-refund-option-${ previewTicket.providerTicketId }`}
                                                    key={previewTicket.providerTicketId}
                                                    style={{ "borderColor": isChecked && previewTicket.eligible ? palette.brandPrimary : palette.overlayBorder }}
                                                >
                                                    <div className="flex min-w-0 items-center gap-2.5">
                                                        <input
                                                            checked={isChecked && previewTicket.eligible}
                                                            className="h-4 w-4 shrink-0 accent-emerald-500"
                                                            disabled={!previewTicket.eligible || submitting}
                                                            onChange={() => toggleTicketSelection( previewTicket.providerTicketId )}
                                                            type="checkbox"
                                                        />
                                                        <div className="flex min-w-0 flex-col">
                                                            <span className="truncate font-bold">
                                                                {title}
                                                            </span>
                                                            <span
                                                                className="truncate text-[11px]"
                                                                style={{ "color": palette.overlayTextSecondary }}
                                                            >
                                                                {route}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {previewTicket.eligible && previewTicket.refundableAmountCents !== undefined
                                                        ? (
                                                            <span className="shrink-0 font-bold">
                                                                <PriceConversion
                                                                    chooseEuro={isEuro}
                                                                    price={previewTicket.refundableAmountCents / 100}
                                                                />
                                                            </span>
                                                        )
                                                        : (
                                                            <span className="shrink-0 rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-semibold">
                                                                {shortNonRefundableBadge( previewTicket.nonRefundableReason )}
                                                            </span>
                                                        )}
                                                </label>
                                            );
                                        } )}
                                    </div>
                                )
                                : null}
                            <div
                                className="flex flex-col gap-2.5 rounded-2xl border p-4 text-xs"
                                data-testid="ticket-refund-summary"
                                style={{
                                    "borderColor":     palette.overlayBorder,
                                    "backgroundColor": isDarkUi ? palette.overlayCardBackgroundStrong : "#FFFFFF",
                                }}
                            >
                                <div className="flex items-baseline justify-between gap-2">
                                    <span style={{ "color": palette.overlayTextSecondary }}>
                                        Prix initial
                                    </span>
                                    <span className="font-semibold">
                                        <PriceConversion
                                            chooseEuro={isEuro}
                                            price={totalOriginalCents / 100}
                                        />
                                    </span>
                                </div>
                                {totalRetainedFeeCents > 0
                                    ? (
                                        <div
                                            className="flex items-baseline justify-between gap-2 text-amber-600 dark:text-amber-400"
                                            data-testid="ticket-refund-fee-line"
                                        >
                                            <span>
                                                Retenue CFF (conditions tarifaires)
                                            </span>
                                            <span className="font-semibold">
                                                {"- "}
                                                <PriceConversion
                                                    chooseEuro={isEuro}
                                                    price={totalRetainedFeeCents / 100}
                                                />
                                            </span>
                                        </div>
                                    )
                                    : (
                                        <div className="flex items-baseline justify-between gap-2 text-emerald-600 dark:text-emerald-400">
                                            <span>
                                                Frais d&apos;annulation
                                            </span>
                                            <span className="inline-flex items-center gap-1 font-semibold">
                                                <IonIcon
                                                    aria-hidden={true}
                                                    className="h-3.5 w-3.5"
                                                    icon={checkmarkCircleOutline}
                                                />
                                                Sans frais
                                            </span>
                                        </div>
                                    )}
                                <div
                                    className="my-1 border-t"
                                    style={{ "borderColor": palette.overlayBorder }}
                                />
                                <div
                                    className="flex items-baseline justify-between gap-2 text-sm font-bold"
                                    data-testid="ticket-refund-total-line"
                                >
                                    <span>
                                        Montant remboursé
                                    </span>
                                    <span className="text-emerald-600 dark:text-emerald-400">
                                        <PriceConversion
                                            chooseEuro={isEuro}
                                            price={totalRefundableCents / 100}
                                        />
                                    </span>
                                </div>
                                {paymentMethodLabel !== ""
                                    ? (
                                        <div className="flex items-baseline justify-between gap-2 pt-1">
                                            <span style={{ "color": palette.overlayTextSecondary }}>
                                                Crédité sur
                                            </span>
                                            <span className="font-semibold">
                                                {paymentMethodLabel}
                                            </span>
                                        </div>
                                    )
                                    : null}
                            </div>
                            <p
                                className="px-1 text-[11px] leading-relaxed"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
                                Une fois le remboursement confirmé, le billet est immédiatement annulé auprès des CFF et ne permet plus de voyager.
                            </p>
                        </>
                    )
                    : null}
            </div>
            <div
                className="mx-auto mt-4 flex w-full max-w-[420px] items-center justify-end gap-2.5 border-t pt-3"
                style={{ "borderColor": palette.overlayBorder }}
            >
                <button
                    className="rounded-xl px-4 py-2.5 text-xs font-bold transition-opacity hover:opacity-80 disabled:opacity-40"
                    data-testid="ticket-refund-cancel"
                    disabled={submitting}
                    onClick={onBack}
                    style={{ "color": palette.overlayTextSecondary }}
                    type="button"
                >
                    Retour au billet
                </button>
                {!loading && eligibleTickets.length > 0
                    ? (
                        <button
                            className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold text-white shadow-sm transition-transform duration-150 ease-out enabled:active:scale-95 disabled:opacity-50"
                            data-testid="ticket-refund-confirm"
                            disabled={submitting || selectedPreviewTickets.length === 0 || totalRefundableCents <= 0}
                            onClick={() => {
                                void handleConfirmRefund();
                            }}
                            type="button"
                        >
                            {submitting
                                ? (
                                    <>
                                        <IonSpinner
                                            className="h-3.5 w-3.5"
                                            name="crescent"
                                        />
                                        <span>
                                            Remboursement en cours...
                                        </span>
                                    </>
                                )
                                : (
                                    <>
                                        <IonIcon
                                            aria-hidden={true}
                                            className="h-4 w-4"
                                            icon={returnDownBackOutline}
                                        />
                                        <span>
                                            {"Rembourser "}
                                            <PriceConversion
                                                chooseEuro={isEuro}
                                                price={totalRefundableCents / 100}
                                            />
                                        </span>
                                    </>
                                )}
                        </button>
                    )
                    : null}
            </div>
        </div>
    );
};
